import { i18nNsFactory } from '@atlas-frontend/i18n';
import React from 'react';
import { useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { Bleed } from '../../common/components';
import {
  FormCheckbox,
  FormDateRangePicker,
  FormField,
  FormInput,
  FormInputNumber,
} from '../../common/form';
import { SystemClassifierFormSelect } from '../../configuration/SystemClassifierFormSelect';
import { GroupCodeFormAutoComplete } from '../GroupCodeFormAutoComplete';
import translations from './ValidationRuleFormFields.translations.json';
import { Divider, Flex } from 'antd';
import './ValidationRuleFormFields.css';
import { SQL_MAX_LENGTH } from '../../common/constants';

const { addTranslations, useTranslation } = i18nNsFactory(
  'validation-rule-inner',
);
addTranslations(translations);

export function ValidationRuleFormFields({
  groupCodes,
}: {
  groupCodes: string[];
}) {
  const { t } = useTranslation();
  const ignoreError = useWatch({ name: 'ignoreError' });

  return (
    <Flex vertical id="validation-wrapper">
      <Flex className="validation-section" gap="middle">
        <Flex vertical gap="middle">
          <Flex gap="small">
            <FormField name="code" label={t('fields.code.label')} required />
            <FormField name="groupCode" label={t('fields.groupCode.label')}>
              <GroupCodeFormAutoComplete
                name="groupCode"
                groupCodes={groupCodes}
              />
            </FormField>
          </Flex>
          <Flex gap="small">
            <FormField name="validPeriod" label={t('fields.validPeriod.label')}>
              <FormDateRangePicker
                name="validPeriod"
                allowEmpty={[true, true]}
              />
            </FormField>
            <FormField name="statusId" label={t('fields.statusId.label')}>
              <StyledFormSelect
                name="statusId"
                code="VALIDATION_RULE_STATUS"
                $width="7rem"
              />
            </FormField>
          </Flex>
        </Flex>
        <FormField
          name="errorDescription"
          label={t('fields.errorDescription.label')}
          style={{ flexGrow: 1 }}
        >
          <FormInput
            className="validation-text"
            name="errorDescription"
            maxLength={2000}
            rows={2}
          />
        </FormField>
      </Flex>
      <Bleed direction="horizontal">
        <Divider />
      </Bleed>
      <Flex className="validation-section" gap="middle">
        <Flex gap="small">
          <FormField
            name="errorLevelId"
            label={t('fields.errorLevelId.label')}
            required
          >
            <StyledFormSelect
              name="errorLevelId"
              code="VALIDATION_ERROR_LEVEL"
              $width="10rem"
            />
          </FormField>
          <FormField
            name="tolerance"
            label={t('fields.tolerance.label')}
            required
          >
            <FormInputNumber name="tolerance" decimalSeparator="," />
          </FormField>
          <FormField
            name="ignoreError"
            label={t('fields.ignoreError.label')}
            style={{ flexGrow: 1 }}
          >
            <FormCheckbox name="ignoreError" />
          </FormField>
        </Flex>
        {ignoreError && (
          <FormField
            name="ignoreReason"
            label={t('fields.ignoreReason.label')}
            style={{ flexGrow: 1 }}
          >
            <FormInput
              className="ignore-reason validation-text"
              name="ignoreReason"
              maxLength={2000}
              rows={2}
            />
          </FormField>
        )}
      </Flex>
      <Bleed direction="horizontal">
        <Divider />
      </Bleed>
      <Flex vertical gap="middle">
        <FormField
          className="validation-rule-condition"
          name="condition"
          label={t('fields.condition.label')}
          style={{ flexGrow: 1 }}
        >
          <FormInput
            name="condition"
            maxLength={SQL_MAX_LENGTH}
            rows={2}
            style={{ fontFamily: 'monospace' }}
          />
        </FormField>
        <Flex className="validation-section" gap="middle">
          <LeftExpressionWrapper
            className="expression-left"
            name="expressionLeft"
            label={t('fields.expressionLeft.label')}
            required
          >
            <FormInput
              name="expressionLeft"
              rows={10}
              style={{ fontFamily: 'monospace' }}
            />
          </LeftExpressionWrapper>
          <FormField
            name="expressionOperatorId"
            label={t('fields.expressionOperatorId.label')}
            required
          >
            <StyledFormSelect
              name="expressionOperatorId"
              code="VALIDATION_RULE_OPERATOR"
              $width="5rem"
            />
          </FormField>
          <RightExpressionWrapper
            className="expression-right"
            name="expressionRight"
            label={t('fields.expressionRight.label')}
            required
          >
            <FormInput
              name="expressionRight"
              rows={10}
              style={{ fontFamily: 'monospace' }}
            />
          </RightExpressionWrapper>
        </Flex>
      </Flex>
    </Flex>
  );
}

const StyledFormSelect = styled(SystemClassifierFormSelect)<{ $width: string }>`
  flex-grow: 1;
  max-height: 32px;
  min-width: ${({ $width }) => $width};
  width: ${({ $width }) => $width};
`;

const ExpressionWrapper = styled(FormField)`
  flex-grow: 1;
`;

const LeftExpressionWrapper = styled(ExpressionWrapper)`
  min-height: 16rem;
`;

const RightExpressionWrapper = styled(ExpressionWrapper)`
  min-height: 10rem;
`;
